import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueuedPopup } from "@/hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { useUserDataFlattened } from "@/modules/user/stores";
import { usePopupTimingByType, useIsHomeDisplayReady } from "@/modules/homeDisplay/hooks";
import { TimingType } from "@/modules/homeDisplay/types";
import type { NotLoginPopupItem } from "@/modules/homeDisplay/types";
import { useCurrencySymbol } from "@/hooks/useUtils";
import { useNavigation, ROUTES } from "@/utils/navigation";
import utils from "@/utils/utils";
import type { UserStatus } from "../components/NotLoginNotRecharge/NotLoginNotRechargeDialog";

const NotLoginNotRechargeDialog = lazy(() =>
  import("../components/NotLoginNotRecharge/NotLoginNotRechargeDialog").then(m => ({
    default: m.NotLoginNotRechargeDialog,
  }))
);

const INTERVAL_STORAGE_KEY = "notlogin_notrecharge_last_shown";

const getLastShownTime = (): number => {
  try {
    const stored = localStorage.getItem(INTERVAL_STORAGE_KEY);
    return stored ? Number(stored) : 0;
  } catch {
    return 0;
  }
};

const markShown = (): void => {
  try {
    localStorage.setItem(INTERVAL_STORAGE_KEY, String(Date.now()));
  } catch {
    // ignore
  }
};

const hasIntervalElapsed = (intervalMinutes: number): boolean => {
  const last = getLastShownTime();
  if (last === 0) return true;
  return Date.now() - last >= intervalMinutes * 1000;
};

export const useNotLoginNotRechargePopup = () => {
  const { isAuthenticated, firstRechargeFlag } = useUserDataFlattened();
  const timing = usePopupTimingByType(TimingType.NOTLOGIN_NOTRECHARGE);
  const isConfigReady = useIsHomeDisplayReady();
  const currencySymbol = useCurrencySymbol();
  const { navigate } = useNavigation();
  const intervalTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [intervalTick, setIntervalTick] = useState(0);
  // 弹窗正在显示时为 true，防止 shouldShow 返回 false 导致队列移除
  const isShowingRef = useRef(false);

  const notLoginItem: NotLoginPopupItem | null = useMemo(() => {
    if (!timing?.enabled || !timing.notLoginItems?.length) return null;
    const enabledItems = timing.notLoginItems.filter(item => item.enabled);
    return enabledItems.length > 0 ? enabledItems[0] : null;
  }, [timing]);

  const userStatus: UserStatus = useMemo(() => {
    if (!isAuthenticated) {
      return utils.hasEverLoggedIn() ? "not_logged_in" : "not_registered";
    }
    return "not_recharged";
  }, [isAuthenticated]);

  const isRechargedUser = isAuthenticated && !!firstRechargeFlag;

  const shouldShowPopup = useCallback(() => {
    // 弹窗正在显示中，始终返回 true 以阻止队列移除
    if (isShowingRef.current) return true;

    if (!isConfigReady) return false;
    if (!notLoginItem) return false;
    if (isRechargedUser) return false;
    if (!hasIntervalElapsed(notLoginItem.intervalMinutes)) return false;
    return true;
  }, [isConfigReady, notLoginItem, isRechargedUser]);

  const handleAction = useCallback(
    (onComplete: () => void) => {
      isShowingRef.current = false;
      markShown();
      onComplete();
      if (!isAuthenticated) {
        if (utils.hasEverLoggedIn()) {
          navigate(ROUTES.LOGIN);
        } else {
          navigate(ROUTES.REGISTER);
        }
      } else {
        navigate(ROUTES.RECHARGE);
      }
    },
    [isAuthenticated, navigate]
  );

  useQueuedPopup({
    popupId: POPUP_CONFIGS.NOTREGISTER_NOTRECHARGE.id,
    priority: POPUP_CONFIGS.NOTREGISTER_NOTRECHARGE.priority,
    shouldShow: shouldShowPopup,
    renderContent: onComplete => {
      if (!notLoginItem) return null;
      isShowingRef.current = true;
      return (
        <Suspense fallback={null}>
          <NotLoginNotRechargeDialog
            visible
            currencySymbol={currencySymbol}
            minBonus={notLoginItem.minBonus}
            maxBonus={notLoginItem.maxBonus}
            gameNames={notLoginItem.gameNames}
            userStatus={userStatus}
            onAction={() => handleAction(onComplete)}
            onClose={() => {
              isShowingRef.current = false;
              markShown();
              onComplete();
            }}
          />
        </Suspense>
      );
    },
    showOnce: false,
    autoTrigger: true,
    dependencies: [
      isConfigReady,
      notLoginItem,
      isAuthenticated,
      firstRechargeFlag,
      intervalTick,
    ],
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/"],
    blockedPaths: [
      ROUTES.LOGIN,
      ROUTES.REGISTER,
      ROUTES.RECHARGE,
    ],
  });

  // 定时器：每 intervalMinutes 递增 tick，触发 shouldShow 重新评估
  useEffect(() => {
    if (!notLoginItem || !isConfigReady || isRechargedUser) {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
        intervalTimerRef.current = null;
      }
      return;
    }

    const intervalMs = notLoginItem.intervalMinutes * 1000;
    if (intervalMs <= 0) return;

    if (intervalTimerRef.current) {
      clearInterval(intervalTimerRef.current);
    }

    intervalTimerRef.current = setInterval(() => {
      setIntervalTick(n => n + 1);
    }, intervalMs);

    return () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
        intervalTimerRef.current = null;
      }
    };
  }, [notLoginItem, isConfigReady, isRechargedUser]);
};
